import { Form } from 'antd';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';

import { userActions } from '@store/user/user.slice';

import { useMutation } from '@hook/common/use-mutation.hook';
import { useDisableMfaMutation, useEnableMfaMutation, useGenerateMfaMutation } from '@hook/mfa/mfa.hooks';
import { useLoadMfaQuery } from '@hook/user/user.hooks';
import { isExist, isString } from '@util/data/is-exist';

export const useToggleMfa = () => {
  const [form] = Form.useForm();
  const qrcode = useRef<HTMLCanvasElement>(null!);
  const { action: generateMfa, state: generateMfaState } = useMutation(useGenerateMfaMutation(), userActions);
  const { action: enableMfa, state: enableMfaState } = useMutation(useEnableMfaMutation(), userActions);
  const { action: disableMfa, state: disableMfaState } = useMutation(useDisableMfaMutation(), userActions);
  const [qrPath, setPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setEnabled] = useState<boolean | undefined>();
  const { data: mfaData } = useLoadMfaQuery(undefined, { refetchOnMountOrArgChange: true, refetchOnFocus: true });
  const [isDisableState, setDisable] = useState(false);

  useEffect(() => void (isExist(mfaData) && setEnabled(mfaData.data)), [mfaData?.data]);
  useEffect(
    () => void (isExist(generateMfaState.data) && setPath(generateMfaState.data.data)),
    [generateMfaState.data?.data],
  );

  const hasMfaPath = isString(qrPath);

  useEffect(
    () =>
      void (
        hasMfaPath &&
        QRCode.toCanvas(qrcode.current, qrPath, {
          width: 300,
          errorCorrectionLevel: 'H',
        }).then(() => setIsLoading(false))
      ),
    [qrPath],
  );

  useEffect(() => {
    setIsLoading(false);

    if (!isExist(disableMfaState.data)) return;

    setEnabled(false);
    setDisable(false);
    setPath('');
    form.resetFields();
  }, [disableMfaState.data, disableMfaState.error]);
  useEffect(() => {
    setIsLoading(false);

    if (!isExist(enableMfaState.data)) return;

    setEnabled(true);
    setDisable(false);
    setPath('');
    form.resetFields();
  }, [enableMfaState.data, enableMfaState.error]);

  const onMfa = () => {
    if (isEnabled && !isDisableState) return setDisable(true);

    setIsLoading(true);

    if (isDisableState) return form.validateFields().then(disableMfa);
    if (!hasMfaPath) return generateMfa(undefined);

    form.validateFields().then(enableMfa);
  };

  return {
    onMfa,
    isLoading,
    isEnabled,
    isDisableState,
    qrcode,
    form,
    hasMfaPath,
    error: enableMfaState.error ?? disableMfaState.error,
  };
};
